'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import {
  getProgramActivityStatus,
  ProgramActivityCard,
  ProgramWorkSubmitDialog,
} from '@/app/components/shared/ProgramActivity';
import { toast } from '@/app/components/ui/use-toast';
import { Link, useLocale } from '@/i18n/routing';
import {
  type ArtistProgramActivity,
  isDuplicateArtistProgramSubmissionError,
  signupActivity,
  submitWork,
} from '../../../_apis';
import { getArtistLocalizedText } from '../../../_localized';

export function ActivityGrid({ activities }: { activities: ArtistProgramActivity[] }) {
  const t = useTranslations('artistProgram.activity');
  const locale = useLocale();
  const [items, setItems] = useState(activities);
  const [active, setActive] = useState<ArtistProgramActivity | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  useEffect(() => setItems(activities), [activities]);

  const text = {
    coverAlt: t('coverAlt'),
    coverFallback: t('coverFallback'),
    ended: t('ended'),
    eventTime: t('eventTime'),
    inProgress: t('inProgress'),
    notStarted: t('notStarted'),
    timePending: t('timePending'),
  };
  const workText = {
    cancel: t('cancel'),
    confirm: t('confirm'),
    submitted: t('submitted'),
    submitting: t('submitting'),
    uploadFailed: t('submitFailed'),
    uploadTitle: t('uploadTitle'),
    workLink: t('workLink'),
    workUrlInvalid: t('workUrlInvalid'),
    workUrlPlaceholder: t('workUrlPlaceholder'),
    title: t('workTitle'),
    titlePlaceholder: t('workTitlePlaceholder'),
    description: t('workDescription'),
    descriptionPlaceholder: t('workDescriptionPlaceholder'),
    titleRequired: t('workTitleRequired'),
    descriptionRequired: t('workDescriptionRequired'),
  };

  return (
    <>
      <div className="grid grid-cols-2 gap-9 sm:grid-cols-1 sm:gap-5">
        {items.map((activity, index) => {
          const id = activity.id ?? '';
          const inProgress = getProgramActivityStatus(activity) === 'in_progress';
          return (
            <ProgramActivityCard
              key={id || index}
              activity={activity}
              detailHref={id ? `/artist-program/activities/${id}` : '/artist-program/activities'}
              title={getArtistLocalizedText(activity.title, locale)}
              text={text}
              actions={
                <div className="grid grid-cols-2 gap-4">
                  <Link
                    href={id ? `/artist-program/activities/${id}` : '/artist-program/activities'}
                    className="inline-flex h-11 items-center justify-center rounded-full border border-white/12"
                  >
                    {t('detail')}
                  </Link>
                  {activity.signed_up ? (
                    <button
                      type="button"
                      disabled={!inProgress}
                      onClick={() => {
                        setActive(activity);
                        setSuccess(false);
                      }}
                      className="rounded-full bg-[#1396ff] disabled:opacity-40"
                    >
                      {t('uploadWork')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={!inProgress}
                      onClick={async () => {
                        try {
                          await signupActivity(id);
                          setItems((current) =>
                            current.map((item) => (item.id === id ? { ...item, signed_up: true } : item)),
                          );
                          toast({ type: 'success', message: t('joinSuccess') });
                        } catch {
                          toast({ type: 'error', message: t('joinFailed') });
                        }
                      }}
                      className="rounded-full bg-[#1396ff] disabled:opacity-40"
                    >
                      {t('join')}
                    </button>
                  )}
                </div>
              }
            />
          );
        })}
      </div>
      <ProgramWorkSubmitDialog
        open={Boolean(active)}
        enableMetadata
        submitting={submitting}
        success={success}
        text={workText}
        onClose={() => setActive(null)}
        onSubmitError={(error) => {
          toast({
            type: 'error',
            message: isDuplicateArtistProgramSubmissionError(error) ? t('duplicateSubmit') : t('submitFailed'),
          });
        }}
        onSubmit={async (workUrl, metadata) => {
          if (!active?.id) return;
          setSubmitting(true);
          try {
            await submitWork({
              activityId: active.id,
              workUrl,
              title: metadata?.title,
              description: metadata?.description,
            });
            setSuccess(true);
          } finally {
            setSubmitting(false);
          }
        }}
      />
    </>
  );
}
